import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from "react-signature-canvas";
import '../../../../Styles/VIC/SignaturePage.scss';
import {
  IconDocument, IconBuilding, IconPackage, IconGear, IconLocation,
  IconSignature, IconStamp, IconFolder, IconImage, IconFile,
  IconTrash, IconRefresh, IconSend, IconCheck, IconWarning, IconClose
} from './Icons';


const GOOGLE_REVIEW_URL = "https://search.google.com/local/writereview?placeid=ChIJC8zRq_cN5kcRmqSH6cO0FQ4";
const COORDOGEC_URL = "https://www.coordogec.fr/";


const SignaturePage = () => {
  const { token } = useParams();
  const sigCanvas = useRef({});
  const fileInputRef = useRef(null);
  const [ndossier, setNdossier] = useState('');
  const [lot, setLot] = useState('');
  const [company, setCompany] = useState('');
  const [operation, setOperation] = useState('');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorCountdown, setErrorCountdown] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [stampFile, setStampFile] = useState(null);
  const [stampPreview, setStampPreview] = useState(null);
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [activeTab, setActiveTab] = useState('signature');
  const notificationRef = useRef(null);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`/api/vic/link-data/${token}`);
        if (!res.ok) {
          throw new Error('Lien invalide ou expiré');
        }
        const data = await res.json();
        setNdossier(data.ndossier);
        setLot(data.lot);
        setCompany(data.company);
        setOperation(data.operation);
        setAddress(data.address);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);


  useEffect(() => {
    if (activeTab === 'signature' && signatureDataUrl && sigCanvas.current) {
      sigCanvas.current.fromDataURL(signatureDataUrl);
    }
  }, [activeTab, signatureDataUrl]);


  const handleTabChange = (tab) => {
    if (tab !== activeTab && activeTab === 'signature' && sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataUrl = sigCanvas.current.getCanvas().toDataURL();
      setSignatureDataUrl(dataUrl);
    }
    setActiveTab(tab);
  };


  const showNotification = (type, message, duration = 3000) => {
    setNotification({ type, message });


    if (duration > 0) {
      setTimeout(() => {
        setNotification(null);
      }, duration);
    }
  };


  const showSuccessWithReview = () => {
    setNotification({
      type: 'success',
      message: `Signature ${stampFile ? 'et tampon sont enregistrés' : 'enregistrée'} avec succès !\nMerci pour votre confiance. Si vous êtes satisfait de notre service, cela nous aiderait beaucoup si vous pouviez nous laisser un avis sur Google.`,
      showReviewButton: true
    });
  };


  // Ferme la notification. Si c'est la notification de succès avec le
  // bouton d'avis Google, la fermeture redirige vers le site Coordogec.
  // Sinon (erreurs, avertissements), on reste simplement sur la page.
  const handleCloseNotification = () => {
    const shouldRedirect = notification?.showReviewButton;
    setNotification(null);
    if (shouldRedirect) {
      window.location.href = COORDOGEC_URL;
    }
  };


  const handleGoToReview = () => {
    window.location.href = GOOGLE_REVIEW_URL;
  };


  const handleGoToWebsite = () => {
    window.location.href = COORDOGEC_URL;
  };


  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        showNotification('error', 'Type de fichier non supporté. Veuillez utiliser JPEG, JPG, PNG, GIF ou PDF.');
        return;
      }


      if (file.size > 10 * 1024 * 1024) {
        showNotification('error', 'Le fichier est trop volumineux. Taille maximale: 10MB.');
        return;
      }


      setStampFile(file);


      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setStampPreview(e.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        setStampPreview(null);
      }
    }
  };


  const handleStampUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };


  const handleRemoveStamp = () => {
    setStampFile(null);
    setStampPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  const handleSubmit = async (e) => {
    if (e) e.preventDefault();


    let currentSignatureDataUrl = null;


    if (signatureDataUrl) {
      currentSignatureDataUrl = signatureDataUrl;
    } else if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      currentSignatureDataUrl = sigCanvas.current.getCanvas().toDataURL();
    }


    if (!currentSignatureDataUrl) {
      showNotification('error', 'Veuillez signer la page avant de la soumettre.');
      return;
    }


    setIsSubmitting(true);
    let formData = new FormData();


    try {
      formData.append("sigLot", currentSignatureDataUrl);


      if (stampFile) {
        formData.append("stampFile", stampFile);
      }


      const res = await fetch(`/api/vic/submit-signature/${token}`, {
        method: "POST",
        body: formData
      });


      if (!res.ok) {
        throw new Error('Échec de l\'enregistrement');
      }


      showSuccessWithReview();
    } catch (err) {
      showNotification('error', `Erreur: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };


  const NotificationComponent = ({ notification }) => {
    if (!notification) return null;


    const getNotificationStyles = () => {
      const baseStyles = {
        success: {
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          icon: <IconCheck />
        },
        error: {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          icon: <IconWarning />
        },
        warning: {
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          icon: <IconWarning />
        }
      };
      return baseStyles[notification.type] || baseStyles.error;
    };


    const styles = getNotificationStyles();


    return (
      <div className="notification__overlay">
        <div className="notification__container">
          <div
            ref={notificationRef}
            className="notification__card"
            style={{ background: styles.background }}
          >
            <button
              className="notification__close"
              onClick={handleCloseNotification}
              aria-label="Fermer"
            >
              <IconClose />
            </button>


            <div className="notification__icon">
              {styles.icon}
            </div>
            <div className="notification__content">
              <p className="notification__message" style={{ whiteSpace: 'pre-line' }}>
                {notification.message}
              </p>
              {notification.showReviewButton && (
                <div className="notification__actions">
                  <button
                    type="button"
                    className="notification__review-button"
                    onClick={handleGoToReview}
                  >
                    Laisser un avis sur Google
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };


  if (loading) {
    return (
      <div className="loading__container">
        <div className="loading__card">
          <div className="loading__spinner"></div>
          <p className="loading__text">
            Chargement des données...
          </p>
        </div>
      </div>
    );
  }


  if (error) {
    return (
      <div className="error__container">
        <div className="error__card">
          <div className="error__icon"><IconWarning /></div>
          <h2 className="error__title">Lien expiré</h2>
          <p className="error__message">{error}</p>
          <p className="error__hint">
            Ce lien de signature n'est plus valide. Contactez-nous si vous pensez qu'il s'agit d'une erreur.
          </p>


          <div className="error__divider">
            <span>En attendant</span>
          </div>


          <p className="error__cta-text">
            Découvrez notre site ou partagez votre expérience avec nous
          </p>


          <div className="error__actions">
            <button
              type="button"
              className="error__button error__button--primary"
              onClick={handleGoToWebsite}
            >
              Visiter notre site
            </button>
            <button
              type="button"
              className="error__button error__button--secondary"
              onClick={handleGoToReview}
            >
              Laisser un avis sur Google
            </button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="signature-page__container">
      <div className="signature-page__main-card">
        <div className="signature-page__header">
          <h1 className="signature-page__title">Signature de VIC</h1>
          <p className="signature-page__subtitle">
            Veuillez signer électroniquement ce document ou télécharger votre tampon
          </p>
        </div>


        <div className="signature-page__info-section">
          <h2 className="signature-page__info-title">Informations du dossier</h2>
          <div className="signature-page__info-grid">
            {ndossier && <div className="signature-page__info-item">
              <div className="signature-page__info-icon"><IconDocument /></div>
              <div className="signature-page__info-text">
                <p className="signature-page__info-label">Dossier</p>
                <p className="signature-page__info-value">{ndossier.split('').join('-')}</p>
              </div>
            </div>}
            {company && <div className="signature-page__info-item">
              <div className="signature-page__info-icon"><IconBuilding /></div>
              <div className="signature-page__info-text">
                <p className="signature-page__info-label">Entreprise</p>
                <p className="signature-page__info-value">{company}</p>
              </div>
            </div>}
            {lot && <div className="signature-page__info-item">
              <div className="signature-page__info-icon"><IconPackage /></div>
              <div className="signature-page__info-text">
                <p className="signature-page__info-label">Lot</p>
                <p className="signature-page__info-value">{lot}</p>
              </div>
            </div>}
            {operation && <div className="signature-page__info-item">
              <div className="signature-page__info-icon"><IconGear /></div>
              <div className="signature-page__info-text">
                <p className="signature-page__info-label">Opération</p>
                <p className="signature-page__info-value">{operation.toUpperCase()}</p>
              </div>
            </div>}
            {address && <div className="signature-page__info-item">
              <div className="signature-page__info-icon"><IconLocation /></div>
              <div className="signature-page__info-text">
                <p className="signature-page__info-label">Adresse</p>
                <p className="signature-page__info-value">{address.toUpperCase()}</p>
              </div>
            </div>}
          </div>
        </div>


        <div className="signature-page__tabs">
          <button
            className={`signature-page__tab ${activeTab === 'signature' ? 'active' : ''}`}
            onClick={() => handleTabChange('signature')}
          >
            <span className="signature-page__tab-icon"><IconSignature /></span>
            <span>Signature</span>
          </button>
          <button
            className={`signature-page__tab ${activeTab === 'stamp' ? 'active' : ''}`}
            onClick={() => handleTabChange('stamp')}
          >
            <span className="signature-page__tab-icon"><IconStamp /></span>
            <span>Tampon</span>
          </button>
        </div>


        <div className="signature-page__content-section">
          {activeTab === 'signature' ? (
            <div className="signature-page__signature-section">
              <div className="signature-page__signature-header">
                <div className="signature-page__signature-icon"><IconSignature /></div>
                <h3 className="signature-page__signature-title">Votre signature</h3>
              </div>
              <div className="signature-page__canvas-container">
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="black"
                  canvasProps={{ className: "signature-page__canvas" }}
                />
                <div className="signature-page__canvas-footer">
                  <p className="signature-page__canvas-hint">
                    Signez dans la zone ci-dessus
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      sigCanvas.current?.clear?.();
                      setSignatureDataUrl(null);
                    }}
                    className="signature-page__clear-button"
                  >
                    <IconRefresh /> Effacer
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="signature-page__stamp-section">
              <div className="signature-page__stamp-header">
                <div className="signature-page__stamp-icon"><IconStamp /></div>
                <h3 className="signature-page__stamp-title">Votre tampon</h3>
              </div>
              <div className="signature-page__stamp-container">
                {!stampFile ? (
                  <div className="signature-page__upload-zone" onClick={handleStampUpload}>
                    <div className="signature-page__upload-icon"><IconFolder /></div>
                    <p className="signature-page__upload-text">
                      Cliquez pour sélectionner votre tampon
                    </p>
                    <p className="signature-page__upload-hint">
                      Formats acceptés: JPEG, JPG, PNG, GIF, PDF (max 10MB)
                    </p>
                  </div>
                ) : (
                  <div className="signature-page__stamp-preview">
                    <div className="signature-page__stamp-preview-header">
                      <div className="signature-page__stamp-file-info">
                        <div className="signature-page__stamp-file-icon">
                          {stampFile.type.startsWith('image/') ? <IconImage /> : <IconFile />}
                        </div>
                        <div className="signature-page__stamp-file-details">
                          <p className="signature-page__stamp-file-name">{stampFile.name}</p>
                          <p className="signature-page__stamp-file-size">
                            {(stampFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveStamp}
                        className="signature-page__remove-stamp-button"
                      >
                        <IconTrash />
                      </button>
                    </div>


                    {stampPreview && (
                      <div className="signature-page__stamp-image-preview">
                        <img src={stampPreview} alt="Prévisualisation du tampon" />
                      </div>
                    )}
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                />
                <div className="signature-page__stamp-footer">
                  <button
                    type="button"
                    onClick={handleStampUpload}
                    className="signature-page__upload-button"
                  >
                    <IconFolder /> {stampFile ? 'Changer le fichier' : 'Sélectionner un fichier'}
                  </button>
                </div>
              </div>
            </div>
          )}


          <div className="signature-page__submit-container">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="signature-page__submit-button"
            >
              {isSubmitting ? (
                <>
                  <div className="signature-page__spinner"></div>
                  <span>Envoi en cours...</span>
                </>
              ) : (
                <>
                  <IconSend />
                  <span>Envoyer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>


      <div className="signature-page__footer">
        <p>Copyright {new Date().getFullYear()} Coordogec • Document sécurisé</p>
      </div>


      <NotificationComponent notification={notification} />
    </div>
  );
};


export default SignaturePage;